import { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

/**
 * One item on the timeline. The timeline places it on the rail; the row only
 * supplies what it draws there and beside it.
 */
export interface CustomerTimelineRow {
  id: string;
  /** ISO timestamp that places the row under its year. */
  dateTime: string;
  /**
   * Date and time already formatted for display by the caller. Left out when
   * the row has no time to show, such as an agent waiting right now.
   */
  dateLabel?: string;
  /**
   * Drawn on the rail, centered on the row's first line of text, such as
   * CustomerTimelineIconNode or CustomerTimelineDotNode.
   */
  node: ReactNode;
  /**
   * Drawn beside the rail, such as CustomerTimelineRowDetails or
   * CustomerTimelineActivityDetails.
   */
  details: ReactNode;
}

export interface CustomerTimelineProps {
  /** Shown first, under an "Upcoming" heading, in the order given. */
  upcomingRows?: readonly CustomerTimelineRow[];
  /** The history, newest first, under a heading for each year. */
  rows: readonly CustomerTimelineRow[];
  /** When present, dim rows outside this set while keeping their place on the rail. */
  highlightedRowIds?: ReadonlySet<string>;
  /** Appends skeleton rows to the loaded timeline while fetching another page. */
  isLoadingMore?: boolean;
}

const fadeStyle =
  "transition-opacity duration-[350ms] motion-reduce:transition-none";
const railSegmentStyle = twMerge(
  "absolute left-1/2 w-px -translate-x-1/2 bg-border-strong",
  fadeStyle,
);

const columnsStyle =
  "grid grid-cols-[2.5rem_2rem_minmax(0,1fr)] sm:grid-cols-[7rem_3rem_minmax(0,1fr)]";

/**
 * The space below each row, set from that row and the next one, the way the
 * transcript spaces its tool calls: activity rows sit closer to each other
 * than interactions do. CustomerTimelineActivityDetails marks a row as
 * activity.
 */
const rowGapStyle =
  "[&>li]:[--row-gap:2rem] [&>li:has([data-customer-timeline-activity])]:[--row-gap:1.5rem] [&>li:has(+li_[data-customer-timeline-activity])]:[--row-gap:1.5rem] [&>li:has([data-customer-timeline-activity]):has(+li_[data-customer-timeline-activity])]:[--row-gap:1rem]";

// The skeleton pulses as one block, rails included, so bars skip `skeletonStyle`'s own pulse.
const skeletonBarStyle = "rounded-full bg-gray-200";

export interface CustomerTimelineSkeletonProps {
  /** Draws a rail into the first placeholder node from the preceding row. */
  isConnected?: boolean;
}

/** Loading placeholder with the same columns and spacing as the timeline. */
export const CustomerTimelineSkeleton: FC<CustomerTimelineSkeletonProps> = (
  props,
) => (
  <section
    role="status"
    aria-label={
      props.isConnected
        ? "Loading more customer activity"
        : "Loading customer timeline"
    }
    className="min-w-0"
    style={{ maskImage: "linear-gradient(to bottom, black 60%, transparent)" }}
  >
    <div
      aria-hidden="true"
      className="animate-pulse motion-reduce:animate-none"
    >
      {[0, 1, 2].map((index) => (
        <div key={index} className={columnsStyle}>
          <div className="col-start-3 row-start-1 mb-1 sm:col-start-1 sm:mb-0 sm:pt-1">
            <div className={twMerge(skeletonBarStyle, "h-3 w-20")} />
          </div>
          <div className="relative col-start-2 row-start-1 row-end-3 flex justify-center sm:row-end-2">
            {props.isConnected && index === 0 && (
              <div className="absolute top-0 left-1/2 h-3 w-px -translate-x-1/2 bg-gray-200" />
            )}
            {index < 2 && (
              <div className="absolute top-6.5 bottom-0 left-1/2 w-px -translate-x-1/2 bg-gray-200" />
            )}
            <div
              className={twMerge(skeletonBarStyle, "relative -mt-0.5 size-7")}
            />
          </div>
          <div
            className={twMerge(
              "col-start-3 row-start-2 min-w-0 sm:row-start-1",
              index < 2 && "pb-8",
            )}
          >
            <div className="max-w-2xl space-y-3 pt-1">
              <div
                className={twMerge(skeletonBarStyle, "h-4 w-3/5 max-w-80")}
              />
              <div className={twMerge(skeletonBarStyle, "h-3 w-28")} />
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

interface YearGroup {
  year: string;
  startIndex: number;
  yearRows: { row: CustomerTimelineRow; index: number }[];
}

/**
 * Owns the layout and the rail. Year labels pin at
 * `--customer-timeline-sticky-top` (default 0), which a page with pinned
 * headers above the list sets to where they end.
 */
export const CustomerTimeline: FC<CustomerTimelineProps> = (props) => {
  const {
    upcomingRows = [],
    rows: historyRows,
    highlightedRowIds,
    isLoadingMore = false,
  } = props;
  // One rail runs through both lists, so they're indexed as one.
  const rows = [...upcomingRows, ...historyRows];
  const yearGroups: YearGroup[] = [];
  for (const [index, row] of rows.entries()) {
    const year =
      index < upcomingRows.length
        ? "Upcoming"
        : DateTime.fromISO(row.dateTime).toFormat("yyyy");
    const group = yearGroups.at(-1);
    if (group?.year === year) {
      group.yearRows.push({ row, index });
    } else {
      yearGroups.push({ year, startIndex: index, yearRows: [{ row, index }] });
    }
  }
  const activeRows = rows.map(
    (row) => highlightedRowIds === undefined || highlightedRowIds.has(row.id),
  );
  // Keep the rail between highlighted nodes, even across dimmed rows.
  const firstActiveIndex = activeRows.indexOf(true);
  const lastActiveIndex = activeRows.lastIndexOf(true);
  const hasRailAbove = (index: number) =>
    index > firstActiveIndex && index <= lastActiveIndex;
  const hasRailBelow = (index: number) =>
    index >= firstActiveIndex && index < lastActiveIndex;

  return (
    // Isolated so the labels' stacking order stays inside the timeline.
    <section aria-label="Customer timeline" className="isolate min-w-0">
      {rows.length === 0 ? (
        <p className="py-8 text-sm text-muted-foreground">No activity yet.</p>
      ) : (
        <ol className="m-0 list-none p-0">
          {yearGroups.map(({ year, startIndex, yearRows }, groupIndex) => (
            <li key={startIndex} className={columnsStyle}>
              {/* Spans the whole year, so it stays pinned until the next
                  year pushes it off. Earlier labels stack higher, so the
                  next label's mask doesn't cut through one on its way out. */}
              <div
                style={{ zIndex: yearGroups.length - groupIndex }}
                className="sticky top-[var(--customer-timeline-sticky-top,0px)] col-start-1 row-start-1 row-end-3 h-8 self-start text-xs leading-5 font-semibold text-foreground before:pointer-events-none before:absolute before:inset-x-0 before:bottom-full before:h-5 before:bg-card after:pointer-events-none after:absolute after:inset-x-0 after:top-5 after:h-3 after:bg-linear-to-b after:from-card after:to-transparent"
              >
                <span className="block bg-card">{year}</span>
              </div>
              <div
                aria-hidden="true"
                className="relative col-start-2 row-start-1 h-8"
              >
                <span
                  className={twMerge(
                    railSegmentStyle,
                    "inset-y-0",
                    !hasRailAbove(startIndex) && "opacity-0",
                  )}
                />
              </div>
              <ol
                className={twMerge(
                  "col-span-full row-start-2 m-0 list-none p-0",
                  rowGapStyle,
                )}
              >
                {yearRows.map(({ row, index }) => {
                  const isDimmed = !activeRows[index];
                  const isLastRow = index === rows.length - 1;
                  const isLoadingContinuation = isLastRow && isLoadingMore;
                  const isLast = isLastRow && !isLoadingMore;

                  return (
                    <li key={row.id} className={columnsStyle}>
                      {row.dateLabel && (
                        <time
                          dateTime={row.dateTime}
                          className={twMerge(
                            "col-start-3 row-start-1 mb-1 text-xs whitespace-nowrap text-muted-foreground sm:col-start-1 sm:mb-0 sm:pt-1",
                            fadeStyle,
                            isDimmed && "opacity-20",
                          )}
                        >
                          {row.dateLabel}
                        </time>
                      )}
                      <div
                        aria-hidden="true"
                        className="relative col-start-2 row-start-1 row-end-3 sm:row-end-2"
                      >
                        {/* Each row draws its half of the rail on either side
                            of its node, which sits on the first line of text,
                            12px down. The lower half runs through the row's
                            bottom gap. */}
                        <span
                          className={twMerge(
                            railSegmentStyle,
                            "top-0 h-3",
                            !hasRailAbove(index) && "opacity-0",
                          )}
                        />
                        <span
                          className={twMerge(
                            railSegmentStyle,
                            "top-3 bottom-0",
                            !hasRailBelow(index) &&
                              !isLoadingContinuation &&
                              "opacity-0",
                            isLoadingContinuation && "bg-gray-200",
                          )}
                        />
                        <span
                          className={twMerge(
                            "absolute top-3 left-1/2 flex -translate-x-1/2 -translate-y-1/2 scale-100 transition-[opacity,scale] duration-[350ms] motion-reduce:transition-none",
                            isDimmed && "scale-95 opacity-0",
                          )}
                        >
                          {row.node}
                        </span>
                      </div>
                      <div
                        className={twMerge(
                          "col-start-3 row-start-2 min-w-0 sm:row-start-1",
                          !isLast && "pb-[var(--row-gap)]",
                          fadeStyle,
                          isDimmed && "opacity-20",
                        )}
                      >
                        {row.details}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </li>
          ))}
        </ol>
      )}
      {isLoadingMore && (
        <CustomerTimelineSkeleton isConnected={rows.length > 0} />
      )}
    </section>
  );
};
